import React, { useState, useEffect } from 'react';
import { sitesAPI, camerasAPI } from '../lib/api';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import {
  ChevronRight,
  ChevronDown,
  MapPin,
  Video,
  Plus,
  Folder
} from 'lucide-react';

export default function TreeSidebar({
  currentProject,
  currentSite,
  currentCamera,
  onSiteSelect,
  onCameraSelect,
  onAddSite,
  onAddCamera,
}) {
  const [sites, setSites] = useState([]);
  const [cameras, setCameras] = useState({});
  const [expandedSites, setExpandedSites] = useState(new Set());
  const [loading, setLoading] = useState(false);

  // Load sites when current project changes
  useEffect(() => {
    if (currentProject) {
      loadSitesForProject(currentProject.id);
    } else {
      setSites([]);
      setCameras({});
    }
  }, [currentProject]);

  // Load cameras for expanded sites
  useEffect(() => {
    expandedSites.forEach(async (siteId) => {
      if (!cameras[siteId]) {
        await loadCamerasForSite(siteId);
      }
    });
  }, [expandedSites]);

  const loadSitesForProject = async (projectId) => {
    try {
      setLoading(true);
      const result = await sitesAPI.getAll(projectId, { limit: 1000 });
      if (result.success) {
        setSites(result.sites || []);
      }
    } catch (error) {
      console.error('Error loading sites:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCamerasForSite = async (siteId) => {
    try {
      const result = await camerasAPI.getAll(siteId, { limit: 1000 });
      if (result.success) {
        setCameras((prev) => ({ ...prev, [siteId]: result.cameras || [] }));
      }
    } catch (error) {
      console.error('Error loading cameras:', error);
    }
  };

  const toggleSite = (siteId) => {
    const newExpanded = new Set(expandedSites);
    if (newExpanded.has(siteId)) {
      newExpanded.delete(siteId);
    } else {
      newExpanded.add(siteId);
    }
    setExpandedSites(newExpanded);
  };

  const handleSiteSelect = (site) => {
    if (onSiteSelect) {
      onSiteSelect(site);
    }
    // Auto-expand when selected
    setExpandedSites((prev) => new Set([...prev, site.id]));
  };

  if (loading) {
    return (
      <div className="w-64 h-full border-r bg-card p-4 flex items-center justify-center">
        <div className="flex flex-col items-center gap-2 text-muted-foreground">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
          <span className="text-sm">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-64 h-full border-r bg-card flex flex-col">
      <div className="p-4 border-b">
        <h3 className="font-semibold flex items-center gap-2">
          <Folder className="h-4 w-4" />
          Site & Camera Tree
        </h3>
      </div>

      <div className="flex-1 overflow-auto p-2">
        {!currentProject ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-4 text-muted-foreground text-sm">
            <p>Select a project to view sites and cameras</p>
          </div>
        ) : (
          <div className="space-y-1">
            {sites.length === 0 ? (
              <div className="text-center p-4 text-muted-foreground text-sm space-y-3">
                <p>No sites found</p>
                {onAddSite && (
                  <Button size="sm" onClick={onAddSite} className="w-full">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Site
                  </Button>
                )}
              </div>
            ) : (
              sites.map((site) => (
                <div key={site.id} className="space-y-1">
                  <div
                    className={cn(
                      "group flex items-center w-full rounded-md hover:bg-accent/50 transition-colors",
                      currentSite?.id === site.id && "bg-accent text-accent-foreground"
                    )}
                  >
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSite(site.id);
                      }}
                    >
                      {expandedSites.has(site.id) ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </Button>

                    <Button
                      variant="ghost"
                      className={cn(
                        "flex-1 justify-start px-2 h-9 font-normal",
                        currentSite?.id === site.id && "font-medium"
                      )}
                      onClick={() => handleSiteSelect(site)}
                    >
                      <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span className="truncate">{site.name}</span>
                    </Button>
                  </div>

                  {/* Cameras Level */}
                  {expandedSites.has(site.id) && (
                    <div className="ml-4 pl-4 border-l space-y-1">
                      {cameras[site.id] && cameras[site.id].length > 0 ? (
                        cameras[site.id].map((camera) => (
                          <div
                            key={camera.id}
                            className={cn(
                              "flex items-center w-full rounded-md px-2 py-1 text-sm transition-colors",
                              currentCamera?.id === camera.id
                                ? "bg-primary/10 text-primary"
                                : "hover:bg-accent/50 text-muted-foreground"
                            )}
                          >
                            <Button
                              variant="ghost"
                              className="w-full justify-start h-8 px-2 font-normal"
                              onClick={() =>
                                onCameraSelect && onCameraSelect(camera, site)
                              }
                            >
                              <Video className="h-3 w-3 mr-2" />
                              <span className="truncate flex-1 text-left">{camera.name}</span>
                              {camera.status && (
                                <span
                                  className={cn(
                                    "w-2 h-2 rounded-full ml-2",
                                    camera.status === 'online' ? "bg-green-500" : "bg-red-500"
                                  )}
                                  title={camera.status}
                                />
                              )}
                            </Button>
                          </div>
                        ))
                      ) : (
                        <div className="px-2 py-2">
                          <p className="text-xs text-muted-foreground mb-2 px-2">No cameras</p>
                          {onAddCamera && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full h-7 text-xs"
                              onClick={() => onAddCamera(site)}
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add Camera
                            </Button>
                          )}
                        </div>
                      )}

                      {/* Add Camera Button (if cameras exist) */}
                      {cameras[site.id] && cameras[site.id].length > 0 && onAddCamera && (
                        <div className="px-2 pb-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full h-7 text-xs text-muted-foreground hover:text-foreground"
                            onClick={() => onAddCamera(site)}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Add Camera
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}

            {sites.length > 0 && onAddSite && (
              <div className="pt-2 mt-2 border-t px-2">
                <Button variant="outline" size="sm" className="w-full" onClick={onAddSite}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Site
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
